/* eslint-disable @typescript-eslint/member-ordering */
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ConfirmationModalComponent } from 'app/shared/modals/confirmation-modal/confirmation-modal.component';
import { ItineraryService } from 'app/shared/itinerary.service';
import { NotificationService } from 'app/shared/notifications/notification.service';
import { UserService } from 'app/pages/com.usblick.common/auth/user.service';
import { RestService } from 'app/shared/rest.service';
import { ProductFactoryService } from 'services/itinerary/products/product-factory.service';
import { FlyProductService } from 'services/itinerary/products/fly-product.service';
import { IProductCreationParams } from 'model/product.interface';
import { FlightAncillary, FlightFareOption, FlightSegment, FlightSyncFareOptionsResponse } from 'model/fly.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { SeatMapComponent } from './seat-map/seat-map.component';
import { CurrencyConverterPurePipe } from 'app/shared/currency-converter-pure.pipe';
import { CarouselBreakpoint } from 'app/shared/utils/infinite-carousel';

interface TramoStep {
  type: 'tramo' | 'escala';
  segmentIndex?: number;
  escalaIndex?: number;
  label: string;
}

interface EscalaInfo {
  city: string;
  country: string;
  airport: string;
  durationMinutes: number;
  startDateTime: string;
  endDateTime: string;
}

interface FareFeatureGroup {
  title: string;
  lines: string[];
}

const ORDINAL_LABELS = ['PRIMER TRAMO', 'SEGUNDO TRAMO', 'TERCER TRAMO', 'CUARTO TRAMO', 'QUINTO TRAMO'];

@Component({
  selector: 'usb-detail-modal-flight-result',
  templateUrl: './detail-modal-flight-result.component.html',
  styleUrls: ['./detail-modal-flight-result.component.scss'],
  providers: [CurrencyConverterPurePipe]
})
export class DetailModalFlightResultComponent implements OnInit {

  private readonly CATEGORY_ID = 5;

  flightData: any; // Datos del vuelo que recibirá
  payload: any; // Payload de búsqueda de vuelos
  arrayResults: any; // Respuesta completa de la búsqueda (searchId, etc)
  clientsSettingAppBooking: any;
  currencyAgency: any;
  currencies: any;

  // Modo "Por tramos": no crear el producto acá — sólo aplicar la tarifa
  // elegida y devolverla al componente que orquesta la selección tramo por
  // tramo, que decide cuándo (recién con todos los tramos elegidos) crear
  // los productos.
  deferCreation = false;

  @Output() productAdded = new EventEmitter<any>();
  @Output() fareSelectedForLeg = new EventEmitter<{ offer: any; fare: any }>();

  processing = false;
  // Índice dentro de getTramoSteps() (mezcla pasos de tipo 'tramo' y 'escala') —
  // sólo decide qué se muestra en el panel "Información" de abajo; el box "Tramo"
  // de arriba siempre muestra la ruta completa.
  selectedStepIndex = 0;

  // Igual patrón que shortcuts.component.ts: tramos tipo "responsive" de owl para el
  // usb-carousel de tarjetas de tarifa — 4 visibles en el ancho del modal (min-width 1240px,
  // ver .modal-lg-flight-detail), menos en pantallas chicas.
  fareCarouselBreakpoints: CarouselBreakpoint[] = [
    { minWidth: 0, items: 1 },
    { minWidth: 480, items: 2 },
    { minWidth: 768, items: 3 },
    { minWidth: 1100, items: 4 }
  ];

  // Familias tarifarias (baseFare + alternativeFares) traídas de /syncProducts
  fareOptions: FlightFareOption[] = [];
  loadingFareOptions = false;
  fareOptionsError = false;

  // Ancillaries (extras pagos del proveedor, ej. valija extra) traídos de
  // /syncProducts con withAncillaries:true. Selección independiente de la
  // tarifa — se arrastra junto al flightOffer elegido en chooseFare().
  ancillaries: FlightAncillary[] = [];
  loadingAncillaries = false;

  private chosenFareId: string | null = null;
  private selectedAncillaryKeys = new Set<string>();

  constructor(
    public bsModalRef: BsModalRef,
    private itineraryService: ItineraryService,
    private notificationService: NotificationService,
    private userService: UserService,
    private restService: RestService,
    private productFactory: ProductFactoryService,
    private flyProductService: FlyProductService,
    private modalService: BsModalService,
    private currencyPipe: CurrencyConverterPurePipe,
  ) { }

  ngOnInit(): void {
    this.loadFareOptions();
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  // Mapa de asientos (solo visualización) de la tarifa que se está mostrando
  // ahora mismo — se abre como un modal aparte, encima de este.
  openSeatMap(): void {
    if (!this.flightData?.bookParam) {
      return;
    }

    this.modalService.show(SeatMapComponent, {
      initialState: {
        flightData: this.flightData,
        providerId: this.flightData?.providerId,
        bookingCode: this.flightData?.bookParam,
      },
      class: 'modal-lg modal-dialog-centered',
      backdrop: 'static',
      keyboard: false,
    });
  }

  // ==========================
  // Familias tarifarias (syncProducts / getFareOptions)
  // ==========================

  loadFareOptions(): void {
    if (!this.flightData) {
      return;
    }

    this.loadingFareOptions = true;
    this.fareOptionsError = false;

    const payload = this.flyProductService.buildFareOptionsPayload(this.flightData, this.payload, {
      userService: this.userService,
    });

    payload.products[0].raw_response = true;

    this.restService.doPost('app', 'syncProducts', payload).subscribe({
      next: (response: { success: boolean; mens: any }) => {

        const flightSync = response?.mens?.results?.find(r => r.category === this.CATEGORY_ID)?.data;

        const syncResult = flightSync?.results?.[0];

        this.fareOptions = this.buildFareOptionsList(syncResult?.fares);
        this.loadingFareOptions = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error obteniendo familias tarifarias:', error);
        this.fareOptionsError = true;
        this.loadingFareOptions = false;
      }
    });

    this.loadAncillaries(payload);
  }

  // Mismo payload que las familias tarifarias pero pidiendo ancillaries
  // (extras) en vez de eso: getFareOptions:false, withAncillaries:true.
  private loadAncillaries(fareOptionsPayload: any): void {
    this.loadingAncillaries = true;

    const ancillariesPayload = JSON.parse(JSON.stringify(fareOptionsPayload));
    ancillariesPayload.products[0].flights.getFareOptions = false;
    ancillariesPayload.products[0].flights.withAncillaries = true;

    this.restService.doPost('app', 'syncProducts', ancillariesPayload).subscribe({
      next: (response: { success: boolean; mens: any }) => {
        const flightSync = response?.mens?.results?.find(r => r.category === this.CATEGORY_ID)?.data;
        const syncResult = flightSync?.results?.[0];

        this.ancillaries = [...(syncResult?.ancillaries || [])].sort(
          (a, b) => (a.priceProf ?? a.price ?? 0) - (b.priceProf ?? b.price ?? 0)
        );
        this.loadingAncillaries = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error obteniendo ancillaries:', error);
        this.loadingAncillaries = false;
      }
    });
  }

  // La tarifa base (isBase: true) y alguna de las alternativas suelen
  // representar la MISMA tarifa (mismo cabin.fareBasis) expresada dos veces
  // — se descarta el duplicado para no mostrar dos tarjetas idénticas.
  private buildFareOptionsList(fares: FlightFareOption[]): FlightFareOption[] {
    const list = fares || [];
    const baseFare = list.find(fare => fare.isBase) || list[0];

    if (!baseFare) {
      return [];
    }

    const baseFareBasis = baseFare.cabin?.fareBasis;
    const others = list.filter(fare =>
      fare.fareId !== baseFare.fareId && (!baseFareBasis || fare.cabin?.fareBasis !== baseFareBasis)
    );

    return [baseFare, ...others];
  }

  getFareLabel(fare: FlightFareOption): string {
    return fare?.brandName || fare?.fareCode || 'Tarifa';
  }

  // Si hay entre 1 y 3 tarifas, se completa con tarjetas "cerradas" (null)
  // hasta llegar a 4, para que el carrusel no quede con huecos / ancho
  // irregular. Con 0 (sin alternativas) o 4+ (ya llena la grilla sola) se
  // deja la lista tal cual.
  get fareOptionsPadded(): (FlightFareOption | null)[] {
    if (this.fareOptions.length === 0 || this.fareOptions.length >= 4) {
      return this.fareOptions;
    }
    return [...this.fareOptions, ...new Array(4 - this.fareOptions.length).fill(null)];
  }

  // Header de cada tarjeta en gama de #eff3f2 (la más barata) a negro (la
  // más cara). El degradado se calcula sobre el ranking de precio de TODO
  // el conjunto de fareOptions (sean 1, 2, 5 o las que sean) — fijo, no
  // sobre qué tarjetas están visibles en el carrusel en este momento, así
  // no cambian de color al desplazarse.
  private readonly fareGradientStart = { r: 0xef, g: 0xf3, b: 0xf2 }; // #eff3f2

  getFareHeaderColor(fare: FlightFareOption): string {
    const fraction = this._fareGradientFraction(fare);
    const { r, g, b } = this.fareGradientStart;
    const red = Math.round(r * (1 - fraction));
    const green = Math.round(g * (1 - fraction));
    const blue = Math.round(b * (1 - fraction));
    return `rgb(${red}, ${green}, ${blue})`;
  }

  // Pasada la mitad del gradiente el fondo ya es demasiado oscuro para
  // texto negro, así que el texto pasa a blanco a partir de ahí.
  getFareHeaderTextColor(fare: FlightFareOption): string {
    return this._fareGradientFraction(fare) > 0.5 ? '#ffffff' : '#000000';
  }

  private _fareGradientFraction(fare: FlightFareOption): number {
    const sorted = [...this.fareOptions].sort((a, b) => this.getFarePrice(a) - this.getFarePrice(b));
    const total = sorted.length;
    const position = sorted.indexOf(fare);
    return total > 1 && position >= 0 ? position / (total - 1) : 0;
  }

  // Mismo criterio que BaseResultComponent.calculatePrice() (categoryId 5):
  // convierte el precio de la tarifa a la moneda de la agencia.
  getFarePrice(fare: FlightFareOption): number {
    return Number(this.currencyPipe.transform(
      fare?.price?.profility?.netPriceBooking,
      fare?.price?.currency,
      this.currencyAgency?.code,
      this.currencies,
    ));
  }

  // Precio de la tarjeta + extras seleccionados (mismos para las 4 tarjetas,
  // no son por tarifa) — así se ve de entrada cuánto suma elegir esa tarifa
  // con los extras ya tildados, sin esperar a confirmar.
  getFareTotalWithAncillaries(fare: FlightFareOption): number {
    return this.getFarePrice(fare) + this.getAncillariesTotal();
  }

  isFareChosen(fare: FlightFareOption): boolean {
    return this.chosenFareId !== null && this.chosenFareId === fare?.fareId;
  }

  // Agrupa fare.services[] (HAND_BAG/CABIN_BAG/CHECKED_BAG/BAGGAGE ->
  // Equipaje, CHANGEABLE/REFUNDABLE -> Cambios y devoluciones) en secciones
  // para la tarjeta. Si por algún motivo no viene ningún service, se arma un
  // resumen a partir de rateClass/voidWindow.
  getFareFeatureGroups(fare: FlightFareOption): FareFeatureGroup[] {
    const services = fare?.services;

    if (!services || services.length === 0) {
      const lines = [
        fare?.rateClass === 'R' ? 'Tarifa reembolsable' : 'Tarifa no reembolsable',
      ];
      if (fare?.voidWindow?.description) {
        lines.push(fare.voidWindow.description);
      }
      return this.withAncillariesGroup([{ title: 'Condiciones', lines }]);
    }

    const baggageCodes = ['HAND_BAG', 'CABIN_BAG', 'CHECKED_BAG', 'BAGGAGE'];
    const conditionCodes = ['CHANGEABLE', 'REFUNDABLE'];

    const baggage = services.filter(s => baggageCodes.includes(s.providerCode || '')).map(s => s.name);
    const conditions = services.filter(s => conditionCodes.includes(s.providerCode || '')).map(s => s.name);
    const others = services
      .filter(s => !baggageCodes.includes(s.providerCode || '') && !conditionCodes.includes(s.providerCode || ''))
      .map(s => s.name);

    const groups: FareFeatureGroup[] = [];
    if (baggage.length) groups.push({ title: 'Equipaje', lines: baggage });
    if (conditions.length) groups.push({ title: 'Cambios y devoluciones', lines: conditions });
    if (others.length) groups.push({ title: 'Otros', lines: others });
    return this.withAncillariesGroup(groups);
  }

  // Agrega (si hay) un grupo "Extras" con los ancillaries tildados arriba —
  // mismo grupo para las 4 tarjetas, así se ve qué se está agregando sin
  // tener que volver a la lista de extras.
  private withAncillariesGroup(groups: FareFeatureGroup[]): FareFeatureGroup[] {
    if (this.selectedAncillaries.length === 0) {
      return groups;
    }

    const lines = this.selectedAncillaries.map(ancillary =>
      `${ancillary.description} (+ ${this.currencyAgency?.code} ${this.getAncillaryPrice(ancillary).toFixed(2)})`
    );
    return [...groups, { title: 'Extras', lines }];
  }

  // Aplica la tarifa elegida sobre la oferta original y recién ahí crea y
  // agrega el producto — así el item que queda en el itinerario reserva con
  // el bookingCode/precio de la tarifa realmente elegida.
  async chooseFare(fare: FlightFareOption | null): Promise<void> {
    if (this.processing || !this.flightData) {
      return;
    }

    if (this.ancillaries.length > 0 && this.selectedAncillaries.length === 0) {
      const continueWithoutAncillaries = await this.confirmContinueWithoutAncillaries();
      if (!continueWithoutAncillaries) {
        return;
      }
    }

    this.chosenFareId = fare?.fareId ?? null;
    const offerToBook = fare ? this.flyProductService.applySelectedFare(this.flightData, fare) : this.flightData;

    // Se arrastran acá (no se tocan precios/payload de reserva todavía: falta
    // confirmar con el proveedor el formato en que espera recibirlos al
    // reservar). Quedan disponibles en el item del itinerario para cuando se
    // defina eso.
    offerToBook.selectedAncillaries = this.selectedAncillaries;

    if (this.deferCreation) {
      this.fareSelectedForLeg.emit({ offer: offerToBook, fare });
      this.closeModal();
      return;
    }

    await this.addFlightToItinerary(offerToBook);
  }

  // ==========================
  // Ancillaries (extras pagos, ej. valija extra)
  // ==========================

  get selectedAncillaries(): FlightAncillary[] {
    return this.ancillaries.filter(ancillary => this.isAncillarySelected(ancillary));
  }

  toggleAncillary(ancillary: FlightAncillary): void {
    const key = this.ancillaryKey(ancillary);
    if (this.selectedAncillaryKeys.has(key)) {
      this.selectedAncillaryKeys.delete(key);
    } else {
      this.selectedAncillaryKeys.add(key);
    }
  }

  isAncillarySelected(ancillary: FlightAncillary): boolean {
    return this.selectedAncillaryKeys.has(this.ancillaryKey(ancillary));
  }

  // El ancillary no trae moneda propia: usa la misma que la tarifa de este
  // mismo vuelo (flightData.price.currency), convertida a la moneda de la
  // agencia igual que getFarePrice().
  getAncillaryPrice(ancillary: FlightAncillary): number {
    return Number(this.currencyPipe.transform(
      ancillary?.priceProf ?? ancillary?.price,
      this.flightData?.price?.currency,
      this.currencyAgency?.code,
      this.currencies,
    ));
  }

  getAncillariesTotal(): number {
    return this.selectedAncillaries.reduce((sum, ancillary) => sum + this.getAncillaryPrice(ancillary), 0);
  }

  // Antes de confirmar una tarifa sin haber elegido ningún extra (habiendo
  // extras disponibles), se consulta — misma UX que ConfirmationModal usa en
  // el resto de la app (cart-sidebar, etc.) para confirmaciones puntuales.
  private confirmContinueWithoutAncillaries(): Promise<boolean> {
    return new Promise(resolve => {
      const modalConfig: ModalOptions = {
        initialState: {
          title: 'Extras disponibles',
          message: 'No seleccionaste ningún extra (equipaje adicional, etc.) para este vuelo. <br /> ' +
            '¿Querés continuar sin agregar ninguno?',
          confirmText: 'Continuar',
          cancelText: 'Elegir extras',
        },
        backdrop: 'static',
        ignoreBackdropClick: true,
      };

      const modalRef = this.modalService.show(ConfirmationModalComponent, modalConfig);
      const sub = modalRef.content.confirmResult.subscribe((result: boolean) => {
        sub.unsubscribe();
        resolve(result);
      });
    });
  }

  getAncillaryTypeLabel(type: string): string {
    switch (type) {
      case 'BAG': return 'Equipaje';
      case 'SEAT': return 'Asiento';
      case 'MEAL': return 'Comida a bordo';
      default: return type;
    }
  }

  // Un mismo code (ej. valija extra) puede venir repetido por pasajero —
  // la clave combina ambos para no pisar selecciones entre pasajeros.
  private ancillaryKey(ancillary: FlightAncillary): string {
    return `${ancillary?.code}_${ancillary?.passengerNumber ?? (ancillary?.passengerNumbers || []).join(',')}`;
  }

  // ==========================
  // Alta del producto
  // ==========================

  private async addFlightToItinerary(flightOffer: any): Promise<void> {
    this.processing = true;
    const loadingToast = this.notificationService.loading('Procesando producto...');
    try {
      const productParams: IProductCreationParams = {
        item: flightOffer,
        product: flightOffer,
        payload: this.payload,
        categoryId: this.CATEGORY_ID,
        othersPerCategory: {
          searchId: this.arrayResults?.searchId,
          userService: this.userService,
        },
      };

      const productService = this.productFactory.getProductService(this.CATEGORY_ID);
      const result = productService.createProduct(productParams);

      this.notificationService.close(loadingToast);

      if (!result) {
        this.notificationService.warning('No se pudo crear el producto');
        this.processing = false;
        return;
      }

      this.itineraryService.add(result).subscribe({
        next: (response: any) => {
          if (response) {
            this.itineraryService.evaluateDateItineraryRange(this.itineraryService.itinerary['items']);
            this.itineraryService.updateItinerary(this.itineraryService.itinerary);
            this.notificationService.success('¡Felicitaciones! Producto agregado con éxito.');
            this.productAdded.emit(result);
            this.closeModal();
          } else {
            this.notificationService.error('No se recibió confirmación del servidor', 'Error');
          }
          this.processing = false;
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error adding product:', error);
          this.notificationService.error('Ocurrió un error al agregar el producto', 'Error');
          this.processing = false;
        }
      });

    } catch (error) {
      this.notificationService.close(loadingToast);
      console.error('Error al procesar:', error);
      this.notificationService.error('Ocurrió un error al procesar la solicitud');
      this.processing = false;
    }
  }

  // ==========================
  // Tramo / segmentos (sólo el tramo de ida — itinerary[0], igual que antes)
  // ==========================

  getAllSegments(): FlightSegment[] {
    return this.flightData?.itinerary?.[0]?.options?.[0]?.segments || [];
  }

  getFirstSegment(): FlightSegment {
    return this.getAllSegments()[0];
  }

  getLastSegment(): FlightSegment {
    const segments = this.getAllSegments();
    return segments[segments.length - 1];
  }

  // Datos de la escala entre el segmento `index` y el siguiente: ubicación
  // (donde se hace la escala, que es la llegada del segmento `index`) y
  // horario de inicio/fin de la espera.
  getEscalaInfo(index: number): EscalaInfo {
    // FlightSegment tipa sólo los campos de referencia de la interfaz; los
    // datos reales de /search traen además city/country en arrival/departure
    // (igual que ya se usa en el template con segment?.arrival?.city), por eso
    // se castea a any acá en vez de ampliar la interfaz sin confirmar el shape.
    const segments: any[] = this.getAllSegments();
    const current = segments[index];
    const next = segments[index + 1];
    return {
      city: current?.arrival?.city,
      country: current?.arrival?.country,
      airport: current?.arrival?.airport,
      durationMinutes: this.calculateLayover(index),
      startDateTime: current?.arrival?.dateTime,
      endDateTime: next?.departure?.dateTime,
    };
  }

  // Arma la tira de pasos "PRIMER TRAMO / ESCALA / SEGUNDO TRAMO / ..." —
  // un paso "tramo" (vuelo) por segmento, con un paso "escala" (también
  // seleccionable) entre cada par de segmentos consecutivos. El box "Tramo"
  // de arriba siempre muestra la ruta completa; esta tira sólo decide qué
  // información se ve en el panel "Información" de abajo.
  getTramoSteps(): TramoStep[] {
    const segments = this.getAllSegments();
    const steps: TramoStep[] = [];

    segments.forEach((_segment, index) => {
      steps.push({
        type: 'tramo',
        segmentIndex: index,
        label: ORDINAL_LABELS[index] || `TRAMO ${index + 1}`,
      });
      if (index < segments.length - 1) {
        steps.push({ type: 'escala', escalaIndex: index, label: 'ESCALA' });
      }
    });

    return steps;
  }

  getSelectedStep(): TramoStep {
    return this.getTramoSteps()[this.selectedStepIndex] || this.getTramoSteps()[0];
  }

  // Segmento de vuelo que muestra el panel "Información" cuando el paso
  // seleccionado es un tramo (no una escala).
  getSelectedSegment(): FlightSegment {
    const step = this.getSelectedStep();
    const index = step?.type === 'tramo' ? step.segmentIndex : 0;
    return this.getAllSegments()[index] || this.getAllSegments()[0];
  }

  getSelectedEscalaInfo(): EscalaInfo {
    return this.getEscalaInfo(this.getSelectedStep()?.escalaIndex ?? 0);
  }

  // Duración de vuelo del segmento, en minutos. El segmento puede traer
  // `duration` en null (visto en datos reales) — en ese caso se calcula a
  // partir de departure/arrival en vez de mostrar vacío.
  getSegmentDuration(segment: any): number {
    if (segment?.duration != null) {
      return segment.duration;
    }
    const departure = segment?.departure?.dateTime;
    const arrival = segment?.arrival?.dateTime;
    if (!departure || !arrival) {
      return 0;
    }
    return Math.round((new Date(arrival).getTime() - new Date(departure).getTime()) / 60000);
  }

  // getTramoSteps() devuelve objetos nuevos en cada ciclo de detección de
  // cambios (no cachea); sin trackBy, el *ngFor que lo recorre destruye y
  // recrea todos los botones en cada ciclo, lo que en algunos casos puede
  // hacer que un click se pierda. trackBy por índice evita esa recreación.
  trackByIndex(index: number): number {
    return index;
  }

  selectStep(index: number): void {
    console.log('[DEBUG tramo-step] selectStep llamado con index=', index,
      'antes=', this.selectedStepIndex);
    this.selectedStepIndex = index;
    console.log('[DEBUG tramo-step] selectedStepIndex después=', this.selectedStepIndex);
  }

  nextStep(): void {
    if (this.selectedStepIndex < this.getTramoSteps().length - 1) {
      this.selectedStepIndex++;
    }
  }

  hasNextStep(): boolean {
    return this.selectedStepIndex < this.getTramoSteps().length - 1;
  }

  prevStep(): void {
    if (this.selectedStepIndex > 0) {
      this.selectedStepIndex--;
    }
  }

  hasPrevStep(): boolean {
    return this.selectedStepIndex > 0;
  }

  getStopsLabel(): string {
    const stops = this.getAllSegments().length - 1;
    if (stops <= 0) return 'directo';
    return stops === 1 ? '1 escala' : `${stops} escalas`;
  }

  calculateLayover(index: number): number {
    const segments = this.getAllSegments();
    if (index >= segments.length - 1) return 0;

    const currentSegment = segments[index];
    const nextSegment = segments[index + 1];

    if (!currentSegment?.arrival?.dateTime || !nextSegment?.departure?.dateTime) return 0;

    const arrival = new Date(currentSegment.arrival.dateTime).getTime();
    const departure = new Date(nextSegment.departure.dateTime).getTime();

    return Math.floor((departure - arrival) / (1000 * 60)); // minutos
  }

  // ==========================
  // Formato
  // ==========================

  formatDuration(minutes: number): string {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatTime(dateTime: string): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) {
      return '';
    }
    // Formato 24 hs (HH:mm), sin a. m./p. m.
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  getItem(item): void {
    console.log(item)
  }
}
